import { useState, useEffect } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { useGooglePlaces } from '../hooks/useGooglePlaces';

export default function RestaurantCSVExport() {
  const [csvData, setCsvData] = useState<string>('');
  const [isReady, setIsReady] = useState(false);
  
  // Fetch all restaurants from all locations
  const { places, loading } = useGooglePlaces(undefined);

  useEffect(() => {
    if (!loading && places.length > 0) {
      // Create CSV content
      const headers = ['Restaurant Name', 'Address'];
      const rows = places.map(restaurant => [
        `"${restaurant.name.replace(/"/g, '""')}"`, // Escape quotes
        `"${restaurant.address.replace(/"/g, '""')}"` // Escape quotes
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      setCsvData(csvContent);
      setIsReady(true);
    }
  }, [places, loading]);

  const handleDownload = () => {
    if (!csvData) {
      console.error('No CSV data available');
      alert('No data to download. Please wait for restaurants to load.');
      return;
    }

    try {
      const element = document.createElement('a');
      const file = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(file);
      
      element.setAttribute('href', url);
      element.setAttribute('download', 'restaurants0622.csv');
      element.style.visibility = 'hidden';
      
      document.body.appendChild(element);
      
      // Trigger download
      element.click();
      
      // Cleanup
      document.body.removeChild(element);
      URL.revokeObjectURL(url);
      
      console.log('Download triggered successfully');
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
      <h3 className="text-lg font-bold text-gray-900 mb-2">Export Restaurants</h3>
      <p className="text-sm text-gray-600 mb-4">
        Download a CSV file with all restaurant names and addresses from the restaurants category page.
      </p>
      
      {loading ? (
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading restaurants...</span>
        </div>
      ) : isReady ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            <strong>{places.length}</strong> restaurants ready to export
          </p>
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 bg-[#f97316] hover:bg-[#ea580c] text-white font-semibold py-2 px-4 rounded-md transition-colors duration-200"
          >
            <Download className="w-4 h-4" />
            Download CSV
          </button>
          
          {/* Preview */}
          <div className="mt-4 p-3 bg-gray-50 rounded border border-gray-200 max-h-64 overflow-y-auto">
            <p className="text-xs font-semibold text-gray-700 mb-2">Preview:</p>
            <pre className="text-xs text-gray-600 whitespace-pre-wrap break-words">
              {csvData.split('\n').slice(0, 6).join('\n')}
              {csvData.split('\n').length > 6 && '\n... and more'}
            </pre>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">No restaurants found</p>
      )}
    </div>
  );
}
